'use es6';

import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { Map as ImmutableMap } from 'immutable';
import { FLINT } from 'HubStyleTokens/colors';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ContentRoutes from 'ContentUtils/Routes';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIList from 'UIComponents/list/UIList';
import UITextInput from 'UIComponents/input/UITextInput';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UIConfirmModal from 'UIComponents/dialog/UIConfirmModal';
import { Text } from 'foundations-components/components/Text';
import { getAbActiveVariant, getAbMaster, getHasAbTestStarted, getABTestID } from 'ContentEditorUI/redux/selectors/abTestSelectors';
import { updatePagesAbTest } from 'ContentEditorUI/redux/actions/abTestActions';
import { getAppStatus } from 'ContentEditorUI/redux/selectors/appStatusSelectors';
import { APP_STATUS } from 'ContentEditorUI/redux/constants';
import { getSubcategory } from 'ContentEditorUI/redux/selectors/baseContentModelSelectors';
import { getCategoryId } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import H2 from 'UIComponents/elements/headings/H2';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const mapStateToProps = state => {
  return {
    abMaster: getAbMaster(state),
    abVariation: getAbActiveVariant(state),
    hasAbTestStarted: getHasAbTestStarted(state),
    abTestId: getABTestID(state),
    appStatus: getAppStatus(state),
    subcategory: getSubcategory(state),
    categoryId: getCategoryId(state)
  };
};
const mapDispatchToProps = {
  updatePagesAbTest
};
const ErrorLabel = ({
  error
}) => error ? /*#__PURE__*/_jsx(FormattedMessage, {
  message: `abTestManager.validationErrorMessage.${error}`
}) : null;
const ActionIcon = ({
  icon = 'delete',
  onClick,
  tooltip = 'deleteVariation'
}) => /*#__PURE__*/_jsx(UIIconButton, {
  onClick: onClick,
  tooltip: /*#__PURE__*/_jsx(FormattedMessage, {
    message: `abTestManager.${tooltip}`
  }),
  use: "transparent",
  placement: "right",
  children: /*#__PURE__*/_jsx(UIIcon, {
    name: icon
  })
});
const VALIDATION_ERRORS = {
  EMPTY_MASTER_NAME: 'emptyMasterVariationName',
  DUPLICATE_VARIATION_NAME: 'hasDuplicateName',
  EMPTY_VARIATION_NAME: 'hasEmptyVariationName'
};
const getVariationError = (variationName, masterVariationName) => {
  if (variationName !== null && !variationName) {
    return VALIDATION_ERRORS.EMPTY_VARIATION_NAME;
  }
  if (variationName === masterVariationName) {
    return VALIDATION_ERRORS.DUPLICATE_VARIATION_NAME;
  }
  return null;
};
class ABTestManagerModal extends Component {
  constructor(props) {
    super(props);
    this.handleMasterVariationNameChange = ({
      target: {
        value
      }
    }) => {
      this.setState({
        masterVariation: {
          name: value,
          nameChanged: true,
          error: value.length === 0 ? VALIDATION_ERRORS.EMPTY_MASTER_NAME : null
        },
        currentVariation: Object.assign({}, this.state.currentVariation, {
          error: getVariationError(this.state.currentVariation.name, value)
        }),
        newVariation: Object.assign({}, this.state.newVariation, {
          error: getVariationError(this.state.newVariation.name, value)
        })
      });
    };
    this.handleVariationNameChange = ({
      target: {
        value
      }
    }) => {
      this.setState({
        currentVariation: {
          name: value,
          nameChanged: true,
          error: getVariationError(value, this.state.masterVariation.name)
        }
      });
    };
    this.handleNewVariationNameChange = ({
      target: {
        value
      }
    }) => {
      this.setState({
        newVariation: {
          name: value,
          error: getVariationError(value, this.state.masterVariation.name)
        }
      });
    };
    this.handleDeleteCurrentVariation = () => {
      this.setState({
        currentVariation: Object.assign({}, this.state.currentVariation, {
          deleted: true
        })
      });
    };
    this.handleUndoDeleteCurrentVariation = () => {
      this.setState({
        currentVariation: Object.assign({}, this.state.currentVariation, {
          deleted: false
        }),
        newVariation: {
          name: null,
          error: null
        }
      });
    };
    this.handleDeleteNewVariation = () => {
      this.setState({
        newVariation: {
          name: null,
          error: null
        }
      });
    };
    this.handleAddVariation = () => {
      this.setState({
        newVariation: {
          name: I18n.text('abTestManager.newVariationName', {
            masterVariationName: I18n.SafeString(this.state.masterVariation.name)
          }),
          error: null
        }
      });
    };
    this.handleSaveChangesClick = () => {
      if (this.state.currentVariation.deleted) {
        this.setState({
          deleteModalOpen: true
        });
      } else {
        this.saveChanges();
      }
    };
    this.saveChanges = () => {
      const {
        currentVariation: {
          deleted: currentVariationDeleted,
          name: currentVariationName,
          nameChanged: currentVariationNameChanged
        },
        masterVariation: {
          name: masterName,
          nameChanged: masterNameChanged
        },
        newVariation: {
          name: newVariationName
        }
      } = this.state;
      this.props.updatePagesAbTest({
        testId: this.props.abTestId,
        deleteAbTest: currentVariationDeleted,
        createNewTest: currentVariationDeleted && newVariationName,
        renameVariationValue: currentVariationNameChanged ? currentVariationName : undefined,
        renameMasterValue: masterNameChanged ? masterName : undefined,
        newVariationName
      });
    };
    this.state = {
      masterVariation: {
        name: props.abMaster.name,
        nameChanged: false,
        error: null
      },
      currentVariation: {
        name: props.abVariation.name,
        nameChanged: false,
        deleted: false,
        error: null
      },
      newVariation: {
        error: null,
        name: null
      },
      deleteModalOpen: false
    };
  }
  getMessage() {
    const {
      hasAbTestStarted
    } = this.props;
    const {
      deleted,
      name
    } = this.state.currentVariation;
    const messageKeyPostfix = hasAbTestStarted ? 'testWillEnd' : deleted ? 'testWillBeDeleted' : 'default';
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: `abTestManager.deleteModal.message.${messageKeyPostfix}`,
      options: {
        variationName: name
      }
    });
  }
  renderMasterVariationName() {
    const {
      name,
      error
    } = this.state.masterVariation;
    return this.props.hasAbTestStarted ? /*#__PURE__*/_jsx("span", {
      className: "m-bottom-2",
      children: name
    }) : /*#__PURE__*/_jsx(UIFormControl, {
      "aria-label": "Original variation name",
      className: "m-bottom-2",
      error: !!error,
      validationMessage: /*#__PURE__*/_jsx(ErrorLabel, {
        error: error
      }),
      children: /*#__PURE__*/_jsx(UITextInput, {
        onChange: this.handleMasterVariationNameChange,
        value: name
      })
    }, "masterVariation");
  }
  renderCurrentVariation() {
    const {
      error,
      name,
      deleted
    } = this.state.currentVariation;
    const iconProps = deleted ? {
      icon: 'undo',
      onClick: this.handleUndoDeleteCurrentVariation,
      tooltip: 'deleteVariationUndo'
    } : {
      onClick: this.handleDeleteCurrentVariation
    };
    return this.props.hasAbTestStarted ? /*#__PURE__*/_jsx("span", {
      className: "m-bottom-2",
      children: name
    }) : /*#__PURE__*/_jsx(UIMediaObject, {
      className: "m-bottom-2",
      itemRight: /*#__PURE__*/_jsx(ActionIcon, Object.assign({}, iconProps)),
      children: /*#__PURE__*/_jsx(UIFormControl, {
        "aria-label": "Current variation name",
        error: !!error,
        validationMessage: /*#__PURE__*/_jsx(ErrorLabel, {
          error: error
        }),
        children: /*#__PURE__*/_jsx(Text, {
          textDecoration: deleted ? 'line-through' : 'none',
          color: deleted ? 'text-core-disabled' : 'text-core-default',
          children: /*#__PURE__*/_jsx(UITextInput, {
            readOnly: deleted,
            onChange: this.handleVariationNameChange,
            value: name,
            style: deleted ? {
              color: FLINT
            } : undefined
          })
        })
      })
    });
  }
  renderNewVariation() {
    const {
      name,
      error
    } = this.state.newVariation;
    if (!name) {
      return null;
    }
    return /*#__PURE__*/_jsx(UIMediaObject, {
      className: "m-bottom-2",
      itemRight: /*#__PURE__*/_jsx(ActionIcon, {
        onClick: this.handleDeleteNewVariation
      }),
      children: /*#__PURE__*/_jsx(UIFormControl, {
        "aria-label": "New variation name",
        error: !!error,
        validationMessage: /*#__PURE__*/_jsx(ErrorLabel, {
          error: error
        }),
        children: /*#__PURE__*/_jsx(UITextInput, {
          onChange: this.handleNewVariationNameChange,
          value: name
        })
      })
    });
  }
  renderAddVariationButton() {
    if (!this.state.currentVariation.deleted || this.state.newVariation.name) {
      return null;
    }
    return /*#__PURE__*/_jsxs(UIButton, {
      className: "m-bottom-2",
      onClick: this.handleAddVariation,
      use: "link",
      children: [/*#__PURE__*/_jsx(UIIcon, {
        name: "add"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "abTestManager.addVariation"
      })]
    }, "addVariationButton");
  }
  renderVariations() {
    return /*#__PURE__*/_jsx(UIFormControl, {
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "abTestManager.variationFields"
      }),
      children: /*#__PURE__*/_jsxs(UIList, {
        children: [this.renderMasterVariationName(), this.renderCurrentVariation(), this.renderNewVariation(), this.renderAddVariationButton()]
      })
    });
  }
  renderABVariationDeleteModal() {
    if (!this.state.deleteModalOpen) {
      return null;
    }
    return /*#__PURE__*/_jsx(UIConfirmModal, {
      confirmLabel: I18n.text('abTestManager.deleteModal.confirm'),
      confirmUse: "danger",
      disableConfirm: this.props.appStatus === APP_STATUS.LOADING,
      description: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'abTestManager.deleteModal.description'
      }),
      match: I18n.text('abTestManager.deleteModal.matcherValue'),
      matcherLabel: I18n.text('abTestManager.deleteModal.matcherLabel'),
      message: this.getMessage(),
      onConfirm: this.saveChanges,
      onReject: () => this.setState({
        deleteModalOpen: false
      }),
      rejectLabel: I18n.text('abTestManager.deleteModal.cancel'),
      use: "danger"
    });
  }
  renderSaveOrEndTestButton() {
    const {
      abMaster,
      categoryId,
      subcategory
    } = this.props;
    if (this.props.hasAbTestStarted) {
      return /*#__PURE__*/_jsx(UIButton, {
        use: "danger",
        href: ContentRoutes.details(abMaster.id, categoryId, subcategory, null, {
          tab: 'testResults'
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "abTestManager.endTestButton"
        })
      });
    }
    const {
      masterVariation,
      currentVariation,
      newVariation
    } = this.state;
    const saveDisabled = masterVariation.error || currentVariation.error || newVariation.error;
    return /*#__PURE__*/_jsx(UIButton, {
      disabled: saveDisabled,
      use: "primary",
      onClick: this.handleSaveChangesClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "abTestManager.confirmButton"
      })
    });
  }
  render() {
    const {
      onClose
    } = this.props;
    return /*#__PURE__*/_jsxs(UIModal, {
      width: 450,
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: onClose
        }), /*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "abTestManager.header"
          })
        })]
      }), /*#__PURE__*/_jsxs(UIDialogBody, {
        children: [this.renderVariations(), this.renderABVariationDeleteModal()]
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [this.renderSaveOrEndTestButton(), /*#__PURE__*/_jsx(UIButton, {
          use: "secondary",
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "abTestManager.cancel"
          })
        })]
      })]
    });
  }
}
ABTestManagerModal.propTypes = {
  abMaster: PropTypes.instanceOf(ImmutableMap).isRequired,
  abVariation: PropTypes.instanceOf(ImmutableMap).isRequired,
  hasAbTestStarted: PropTypes.bool.isRequired,
  abTestId: PropTypes.number.isRequired,
  updatePagesAbTest: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  appStatus: PropTypes.string,
  subcategory: PropTypes.string.isRequired,
  categoryId: PropTypes.number.isRequired
};
export default connect(mapStateToProps, mapDispatchToProps)(ABTestManagerModal);